<script setup lang="ts">
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import OrderStatusBadge from './OrderStatusBadge.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    index: number;
    modelValue?: OrderStatus;
    disabled?: boolean;
    // ホワイトリスト的に選択肢を指定する場合
    availableStatus?: OrderStatus[];
    // ブラックリスト的に選択肢を指定する場合
    exceptStatus?: OrderStatus[];
  }>(),
  { modelValue: OrderStatus.Unknown, availableStatus: undefined, exceptStatus: undefined }
);

const emits = defineEmits<{ (e: 'update:modelValue', value: OrderStatus) }>();

const isDropdownOpening = ref(false);

const containerId = computed(() => `order-status-select-input__container-${props.index}`);
const dropdownId = computed(() => `order-status-select-input__dropdown-${props.index}`);

const dropdownWidth = computed(() => document.getElementById(containerId.value)?.clientWidth ?? 0);

const orderStatusOptions = computed(() => {
  if (props.availableStatus) {
    return props.availableStatus;
  }
  if (props.exceptStatus) {
    return Object.fromEntries(Object.entries(OrderStatus).filter(([key]) => !(props.exceptStatus ?? []).includes(key as OrderStatus)));
  }
  return OrderStatus;
});

/**
 * アウトサイドクリックでドロップダウンを閉じる
 * @param event - MouseEvent
 */
const closeDropdown = (event: MouseEvent) => {
  const target = event.target;
  if (!(target instanceof Element) || target.closest(`#${containerId.value}`) || target.closest(`#${dropdownId.value}`)) return;
  isDropdownOpening.value = false;
};

/**
 * ドロップダウンを切り替える
 */
const toggleDropdown = () => {
  isDropdownOpening.value = !isDropdownOpening.value;
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown);
});

/**
 * 選択肢をクリックした際の動作
 * @param value - 選択したOrderStatus
 */
const onSelectOption = (value: OrderStatus) => {
  isDropdownOpening.value = false;
  emits('update:modelValue', value);
};
</script>
<template>
  <button
    :id="containerId"
    class="order-status-select-input__container"
    :class="{ 'order-status-select-input--disabled': disabled }"
    @click="toggleDropdown"
  >
    <OrderStatusBadge :order-status-str="modelValue" />
    <SvgLoader name="arrowDown" />
  </button>
  <div v-if="isDropdownOpening && !disabled" :id="dropdownId" class="order-status-select-input__dropdown">
    <template v-for="orderStatus in orderStatusOptions" :key="orderStatus">
      <button class="order-status-select-input__option" @click="onSelectOption(orderStatus)">
        <OrderStatusBadge :order-status-str="orderStatus" />
      </button>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.order-status-select-input {
  &--disabled {
    cursor: not-allowed;
  }
  &__container {
    display: grid;
    grid-template-columns: min-content min-content;
    align-items: center;
    gap: 4px;
  }

  &__dropdown {
    position: fixed;
    width: v-bind('`${dropdownWidth + 8/* スクロールバーの分余裕を持つ */}px`');
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050;
    overflow-y: auto;
    max-height: 100px;
    box-sizing: border-box;
    display: grid;
    gap: 8px;
    padding: 4px;
  }
}
</style>
